// Alert history and acknowledgement. Requirements 5, 6.
//
// Lists the alert events raised for the configured market target and lets the operator
// acknowledge each one. Acknowledgement is a per-alert action that records who dismissed it.

import { useCallback, useEffect, useState } from "react";

import { useApi } from "../api/useApi";
import type { AlertEvent, MarketTarget } from "../types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function Alerts() {
  const api = useApi();
  const [target, setTarget] = useState<MarketTarget | null>(null);
  const [alerts, setAlerts] = useState<AlertEvent[]>([]);
  const [status, setStatus] = useState("");

  const load = useCallback(
    async (marketTargetId: string) => {
      try {
        const events = await api.listAlerts(marketTargetId);
        setAlerts(events);
      } catch (err) {
        setStatus(err instanceof Error ? err.message : "Failed to load alerts");
      }
    },
    [api],
  );

  useEffect(() => {
    void api.listMarketTargets().then((targets) => {
      if (targets[0]) {
        setTarget(targets[0]);
        void load(targets[0].market_target_id);
      }
    });
  }, [api, load]);

  const acknowledge = async (alertId: string) => {
    if (!target) return;
    setStatus("");
    try {
      await api.acknowledgeAlert(target.market_target_id, alertId);
      await load(target.market_target_id);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Acknowledge failed");
    }
  };

  if (!target) {
    // The nav already names this page, so lead with the state rather than a title.
    return (
      <section className="space-y-2">
        <p className="text-muted-foreground">No market target configured.</p>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      {alerts.length === 0 ? (
        <p className="text-muted-foreground">No alerts have been raised.</p>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div>
              <Table className="block md:table">
                <TableHeader className="hidden md:table-header-group">
                  <TableRow>
                    <TableHead>Raised</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Drawdown</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Max margin</TableHead>
                    <TableHead>Close</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody className="block divide-y divide-border md:table-row-group">
                  {alerts.map((a) => (
                    <TableRow key={a.alert_id} data-testid="alert-row" className="grid grid-cols-2 gap-x-4 gap-y-3 py-4 md:table-row md:p-0">
                      <TableCell className="col-span-2 block p-0 text-xs text-muted-foreground md:table-cell md:p-2">
                        {a.created_at}
                      </TableCell>
                      <TableCell className="flex items-center justify-between p-0 font-medium md:table-cell md:p-2"><span className="text-xs font-normal text-muted-foreground md:hidden">Symbol</span>{a.symbol}</TableCell>
                      <TableCell className="flex items-center justify-between p-0 tabular-nums md:table-cell md:p-2">
                        <span className="text-xs text-muted-foreground md:hidden">Drawdown</span>
                        {a.drawdown_percent.toFixed(2)}%
                      </TableCell>
                      <TableCell className="flex items-center justify-between p-0 tabular-nums md:table-cell md:p-2">
                        <span className="text-xs text-muted-foreground md:hidden">Level</span>
                        {a.alert_level_percent.toFixed(2)}%
                      </TableCell>
                      <TableCell className="flex items-center justify-between p-0 tabular-nums md:table-cell md:p-2">
                        <span className="text-xs text-muted-foreground md:hidden">Max margin</span>
                        {a.maximum_margin_percent.toFixed(2)}%
                      </TableCell>
                      <TableCell className="flex items-center justify-between p-0 tabular-nums md:table-cell md:p-2"><span className="text-xs text-muted-foreground md:hidden">Close</span>{a.closing_price}</TableCell>
                      <TableCell className="flex items-center justify-between p-0 md:table-cell md:p-2">
                        <span className="text-xs text-muted-foreground md:hidden">Status</span>
                        <Badge variant={a.acknowledged ? "secondary" : "default"}>
                          {a.acknowledged ? "Acknowledged" : "Open"}
                        </Badge>
                      </TableCell>
                      <TableCell className="col-span-2 flex justify-end p-0 md:table-cell md:p-2">
                        {!a.acknowledged && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void acknowledge(a.alert_id)}
                          >
                            Acknowledge
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
      {status && <p className="status text-sm text-muted-foreground">{status}</p>}
    </section>
  );
}
